// Which games a list or a count includes: plain data, carried in the
// library's URL query. Declared here so the store's queries take it; each
// criterion (`@chess/api-games/filter`, `criteria/`) parses, formats and
// matches its own field there.
import type { GameSummary } from './game.ts';
import type { MiddlegameTurn } from './turns.ts';
import type { Conversion, EndingResult, EndingType, GameBlunder, GameColour, GameEnding, GameOutcome, GroundCause, MiddlegamePosition, MiddlegameResult, RatingBand, StageChange, TimeClass } from './classification.ts';

/**
 * One value or several. A criterion offered as choices takes several at once
 * ("won or drawn"): a game passes when it matches any of them, and every set
 * criterion must pass. A link may still set one value.
 */
export type OneOrMore<T> = T | readonly T[];

/** The values of a field, as a list: none, one or several. */
export function valuesOf<T>(value: OneOrMore<T> | undefined): T[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? [...(value as readonly T[])] : [value as T];
}

/**
 * Every field optional; an empty filter includes every game. `ids` is the
 * filter engine's own; every other field is a criterion's.
 */
export interface GameFilter {
  /** Only these games; set by resolving `blunder`, `endingType`, `endingResult`, `middlegameResult`, `groundCause`, `middlegamePosition`, `conversion`, `stageChange`, `middlegameTurn` and `mistake`, never carried in the URL. */
  ids?: readonly string[];

  // Each criterion's field; parsed, formatted and matched by its file in
  // `@chess/api-games/filter` (`criteria/<file>`).
  // criteria/blunder.ts
  /** Games with any of these kinds of blunder; resolved to `ids` (the store's `resolveFilter`) before matching. */
  blunder?: OneOrMore<GameBlunder>;
  // criteria/colour.ts
  /** The colour the owner played. */
  colour?: OneOrMore<GameColour>;
  // criteria/ending.ts
  ending?: OneOrMore<GameEnding>;
  // criteria/ending-type.ts
  /** Games whose ending was of any of these kinds (`GameFacts.endingType`); resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  endingType?: OneOrMore<EndingType>;
  // criteria/ending-result.ts
  /** Games whose ending result is any of these (`GameFacts.endingResult`); resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  endingResult?: OneOrMore<EndingResult>;
  // criteria/middlegame-result.ts
  /** Games whose middlegame result is any of these (`GameFacts.middlegameResult`); resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  middlegameResult?: OneOrMore<MiddlegameResult>;
  // criteria/ground-cause.ts
  /** Games whose middlegame went wrong for any of these causes (`GameFacts.groundCause`); resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  groundCause?: OneOrMore<GroundCause>;
  // criteria/middlegame-position.ts
  /** Games whose middlegame the reader played in any of these kinds of position (`GameFacts.middlegamePositions`); resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  middlegamePosition?: OneOrMore<MiddlegamePosition>;
  // criteria/conversion.ts
  /** Games in any of these sets of a middlegame advantage (`Conversion`: stood winning, or where the lead went), from `GameFacts.conversion`; resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  conversion?: OneOrMore<Conversion>;
  // criteria/stage-change.ts
  /** Games whose score change across a stage falls in any of these bands (`StageChange`, e.g. `middlegame-lost`, from `openingScore` and `stageSwings`); resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  stageChange?: OneOrMore<StageChange>;
  // criteria/middlegame-turn.ts
  /** Games whose reader gave up at least `MIDDLEGAME_TURN_PAWNS` with their middlegame moves in any of these move-number bands (`middlegameTurnsOf`, from `stageCurves`); resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  middlegameTurn?: OneOrMore<MiddlegameTurn>;
  // criteria/mistake.ts
  /** Games with a blunder or a miss in any of these groups, each `<kind>-<dimension>-<value>` (e.g. `blunder-motif-fork`, `blunder-motif-fork/knight`, `miss-clock-under1`; `mistakeKeys` in `@chess/api-mistakes`, from `GameFacts.mistakes` and the `mistakes.motifs` service); resolved to `ids` (the store's `resolveFilter`) like `blunder`. */
  mistake?: OneOrMore<string>;
  // criteria/opening.ts
  /**
   * The opening's family or a deeper name, matched as a prefix of the game's
   * family or its full name, ignoring case, punctuation and possessives
   * ("Caro Kann" finds Caro-Kann Defense, "Kings Gambit" King's Gambit).
   */
  opening?: string;
  // criteria/opponent.ts
  /** Part of the opponent's name, case-insensitive. */
  opponent?: string;
  // criteria/outcome.ts
  /** The owner's outcome. */
  outcome?: OneOrMore<GameOutcome>;
  // criteria/rating.ts
  rating?: OneOrMore<RatingBand>;
  // criteria/time-class.ts
  timeClass?: OneOrMore<TimeClass>;
  // criteria/variation.ts
  /**
   * Exactly this variation, as `openingVariation` writes it ("Sicilian
   * Defense: Alapin Variation"; the family alone is its main line only),
   * compared folded. The openings screen links with it, so a row's count is
   * the library's.
   */
  variation?: string;
}

/** What the filter reads: a summary, plus the PGN (its headers suffice) for the opening and the ending, the id for `ids`. */
export type FilterableGame = Pick<GameSummary, 'owner' | 'white' | 'black' | 'result' | 'timeControl'> & { pgn?: string; id?: string; playedAt?: string };
